/**
 * Admin API client for @myvault/shared/api
 *
 * Provides type-safe API calls for admin dashboard features.
 * Uses the base HTTP client from @myvault/api-client.
 */

// =============================================================================
// Types
// =============================================================================

export interface DashboardStats {
  users: {
    total: number;
    active: number;
    newThisMonth: number;
  };
  documents: {
    total: number;
    uploadedThisMonth: number;
    storageUsedBytes: number;
  };
  tenants?: {
    total: number;
    active: number;
  };
  accessGrants?: {
    totalActive: number;
    pendingApproval: number;
  };
  compliance?: {
    pendingRequests: number;
    inProgressRequests: number;
    overdueRequests: number;
    completedThisMonth: number;
  };
}

export interface ServiceHealth {
  name: string;
  status: 'up' | 'degraded' | 'down';
  latencyMs: number;
  message?: string;
  lastCheck: string;
}

export interface SystemHealth {
  status: 'healthy' | 'degraded' | 'unhealthy';
  services: ServiceHealth[];
  metrics?: SystemMetrics;
  timestamp: string;
}

export interface UserListParams {
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  role?: string;
  status?: string;
}

export interface UserListResponse {
  items: AdminUser[];
  data?: AdminUser[]; // Alias for backward compatibility
  total: number;
  page: number;
  perPage: number;
  pages: number;
  pageSize?: number;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalItems?: number;
    totalPages: number;
  };
}

export interface TenantListResponse {
  data: Array<{
    id: string;
    name: string;
    status: string;
    userCount: number;
    documentCount: number;
    storageUsedBytes: number;
    createdAt: string;
  }>;
  total: number;
}

export interface ComplianceStats {
  gdprRequests: {
    pending: number;
    completed: number;
    overdue: number;
  };
  dataRetention: {
    documentsToDelete: number;
    nextPurgeDate: string;
  };
  auditLogs: {
    totalToday: number;
    suspiciousActivities: number;
  };
}

export interface SystemMetrics {
  cpu: { usage: number; cores: number };
  memory: { used: number; total: number; percentage: number };
  disk: { used: number; total: number; percentage: number };
  network: { bytesIn: number; bytesOut: number };
  // Additional metrics used by SystemHealth component
  cpuUsage?: number;
  memoryUsage?: number;
  diskUsage?: number;
  activeConnections?: number;
  requestsPerMinute?: number;
  errorRate?: number;
}

export type MaintenanceType = 'cache_clear' | 'db_vacuum' | 'log_rotation' | 'backup' | 'cleanup';

export interface MaintenanceTask {
  id: string;
  taskId?: string;
  name: string;
  description: string;
  status: 'idle' | 'running' | 'completed' | 'failed';
  lastRun?: string;
  nextRun?: string;
  duration?: number;
  type?: MaintenanceType;
  scope?: string;
}

export interface MaintenanceStatus {
  tasks: MaintenanceTask[];
  isMaintenanceMode: boolean;
  // Task-specific status fields
  status?: 'idle' | 'running' | 'completed' | 'failed';
  progress?: number;
  message?: string;
}

// Admin User type for admin panel
export interface AdminUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  role: string;
  roles: string[];
  personas: string[];
  mfaEnabled: boolean;
  status: string;
  isActive: boolean;
  isLocked: boolean;
  lockReason?: string;
  emailVerified?: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt?: string;
  tenantId: string;
  tenantName?: string;
  tenant?: {
    id: string;
    name: string;
  };
  loginCount?: number;
  preferences?: Record<string, unknown>;
}

export interface UserActivityLog {
  id: string;
  userId: string;
  action: string;
  resource: string;
  resourceId?: string;
  ipAddress: string;
  userAgent: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

// Verification types
export type VerificationStatus = 'pending' | 'in_review' | 'approved' | 'rejected' | 'expired' | 'needs_info';
export type VerificationType = 'professional_license' | 'identity' | 'business' | 'afm_registration' | 'professional' | 'delegate';

export interface VerificationDocument {
  id: string;
  type: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedAt?: string;
  status?: 'pending' | 'verified' | 'rejected';
  url?: string;
  verified?: boolean;
  verificationNotes?: string;
}

export interface VerificationRequest {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  tenantId?: string;
  tenantName?: string;
  type: VerificationType;
  status: VerificationStatus;
  documents: VerificationDocument[];
  submittedAt: string;
  updatedAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  notes?: string;
  rejectionReason?: string;
}

export interface VerificationStats {
  total: number;
  pending: number;
  pendingCount: number;
  inReviewCount: number;
  approvedToday: number;
  rejectedToday: number;
}

// =============================================================================
// GDPR Request Types
// =============================================================================

export type GDPRRequestType = 'access' | 'rectification' | 'erasure' | 'portability' | 'restriction' | 'objection';
export type GDPRRequestStatus = 'pending' | 'in_progress' | 'completed' | 'rejected';

export interface GDPRRequest {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  tenantName?: string;
  type: GDPRRequestType;
  status: GDPRRequestStatus;
  requestedAt: string;
  createdAt?: string;
  dueDate: string;
  completedAt?: string;
  notes?: string;
  assignedTo?: string;
}

export interface GDPRDashboardSummary {
  pendingRequests: number;
  inProgressRequests: number;
  completedThisMonth: number;
  overdueRequests: number;
  averageCompletionDays: number;
}

export interface ComplianceFinding {
  id: string;
  title?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved';
  createdAt: string;
  resolvedAt?: string;
}

export interface ComplianceSummary {
  overallScore: number;
  gdprCompliance?: number;
  openFindings: number;
  totalFindings?: number;
  criticalFindings: number;
  lastAuditDate?: string;
}

export interface ComplianceReport {
  id: string;
  name: string;
  type: string;
  generatedAt: string;
  status: 'generating' | 'ready' | 'failed';
  downloadUrl?: string;
  summary?: ComplianceSummary;
  findings?: ComplianceFinding[];
}

// =============================================================================
// Order Types
// =============================================================================

export type OrderStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'refunded' | 'cancelled' | 'partially_refunded';
export type OrderType = 'subscription' | 'upgrade' | 'addon' | 'renewal';
export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'refunded' | 'succeeded';

export interface OrderItem {
  id: string;
  name?: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  totalPrice?: number;
  total?: number;
  periodStart?: string;
  periodEnd?: string;
}

export interface Order {
  id: string;
  orderNumber?: string;
  invoiceId?: string;
  userId: string;
  userEmail: string;
  userName: string;
  tenantId?: string;
  tenantName?: string;
  type: OrderType;
  status: OrderStatus;
  paymentStatus?: PaymentStatus;
  items: OrderItem[];
  subtotal: number;
  tax: number;
  total: number;
  currency: string;
  paymentMethod?: string;
  paymentMethodLast4?: string;
  invoiceUrl?: string;
  createdAt: string;
  completedAt?: string;
  refundedAt?: string;
  refundAmount?: number;
  refundReason?: string;
  notes?: string;
}

export interface OrderStats {
  total: number;
  revenue: number;
  totalRevenue: number;
  refundedAmount: number;
  pendingCount: number;
  failedCount: number;
  completedToday: number;
}

// =============================================================================
// Support Ticket Types
// =============================================================================

export type SupportTicketStatus = 'open' | 'pending' | 'in_progress' | 'resolved' | 'closed' | 'pending_user';
export type SupportTicketPriority = 'low' | 'medium' | 'high' | 'urgent';
export type SupportTicketCategory = 'technical' | 'billing' | 'account' | 'feature' | 'other';
export type SupportSenderType = 'user' | 'support' | 'system' | 'agent';

export interface SupportTicketAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
  uploadedAt?: string;
}

export interface SupportTicketMessage {
  id: string;
  ticketId: string;
  senderId: string;
  senderName: string;
  senderType: SupportSenderType;
  content: string;
  attachments?: SupportTicketAttachment[];
  createdAt: string;
  isInternal?: boolean;
}

export interface SupportTicket {
  id: string;
  ticketNumber?: string;
  userId: string;
  userEmail: string;
  userName: string;
  tenantId?: string;
  tenantName?: string;
  subject: string;
  description: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  status: SupportTicketStatus;
  assignedTo?: string;
  assignedToName?: string;
  messages: SupportTicketMessage[];
  messageCount?: number;
  attachmentCount?: number;
  createdAt: string;
  updatedAt: string;
  firstResponseAt?: string;
  resolvedAt?: string;
  closedAt?: string;
  slaDeadline?: string;
}

export interface SupportTicketStats {
  total: number;
  open: number;
  openTickets: number;
  urgentTickets: number;
  pendingTickets: number;
  resolvedToday: number;
  averageResolutionHours: number;
}

// =============================================================================
// API Client Base
// =============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const API_BASE_URL = (typeof window !== 'undefined' && (window as any).__API_URL__) || '/api/v1';

const TOKEN_KEY = 'myvault_access_token';

function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Convert snake_case keys to camelCase recursively
 */
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function camelizeKeys(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(camelizeKeys);
  }
  if (obj !== null && typeof obj === 'object' && obj.constructor === Object) {
    return Object.keys(obj).reduce(
      (acc, key) => {
        const camelKey = snakeToCamel(key);
        acc[camelKey] = camelizeKeys(obj[key]);
        return acc;
      },
      {} as Record<string, unknown>
    );
  }
  return obj;
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = getStoredToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return camelizeKeys(data) as T;
}

// =============================================================================
// Admin API
// =============================================================================

export const adminApi = {
  /**
   * Get dashboard statistics
   */
  getDashboardStats: async (): Promise<DashboardStats> => {
    return fetchApi<DashboardStats>('/admin/stats');
  },

  /**
   * Get system health status
   */
  getSystemHealth: async (): Promise<SystemHealth> => {
    return fetchApi<SystemHealth>('/admin/health');
  },

  /**
   * List users (admin view)
   */
  listUsers: async (params: UserListParams = {}): Promise<UserListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set('q', params.search); // Backend uses 'q' for search
    if (params.page) searchParams.set('page', String(params.page));
    if (params.pageSize) searchParams.set('per_page', String(params.pageSize));
    if (params.sortBy) searchParams.set('sort_by', params.sortBy);
    if (params.sortOrder) searchParams.set('sort_order', params.sortOrder);
    if (params.role) searchParams.set('role', params.role);
    if (params.status) searchParams.set('status_filter', params.status);

    const query = searchParams.toString();
    return fetchApi<UserListResponse>(`/admin/users${query ? `?${query}` : ''}`);
  },

  /**
   * Get compliance statistics
   */
  getComplianceStats: async (): Promise<ComplianceStats> => {
    return fetchApi<ComplianceStats>('/admin/compliance/stats');
  },

  /**
   * Get audit logs
   */
  getAuditLogs: async (params: {
    startDate?: string;
    endDate?: string;
    userId?: string;
    action?: string;
    page?: number;
    pageSize?: number;
  } = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return fetchApi(`/admin/audit-logs${query ? `?${query}` : ''}`);
  },

  /**
   * Trigger maintenance task
   */
  triggerMaintenance: async (taskOrOptions: string | { type: MaintenanceType; scope?: string }): Promise<MaintenanceTask> => {
    if (typeof taskOrOptions === 'string') {
      return fetchApi<MaintenanceTask>(`/admin/maintenance/${taskOrOptions}/trigger`, {
        method: 'POST',
      });
    }
    return fetchApi<MaintenanceTask>('/admin/maintenance/trigger', {
      method: 'POST',
      body: JSON.stringify(taskOrOptions),
    });
  },

  /**
   * Get maintenance status
   */
  getMaintenanceStatus: async (taskId?: string): Promise<MaintenanceStatus> => {
    const endpoint = taskId ? `/admin/maintenance/${taskId}/status` : '/admin/maintenance/status';
    return fetchApi<MaintenanceStatus>(endpoint);
  },

  /**
   * Get verifications list
   */
  getVerifications: async (params: {
    status?: VerificationStatus;
    type?: VerificationType;
    page?: number;
    pageSize?: number;
  } = {}): Promise<{ data: VerificationRequest[]; total: number }> => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return fetchApi(`/admin/verifications${query ? `?${query}` : ''}`);
  },

  /**
   * Get verification stats
   */
  getVerificationStats: async (): Promise<VerificationStats> => {
    return fetchApi<VerificationStats>('/admin/verifications/stats');
  },

  /**
   * Update verification status
   */
  updateVerification: async (
    verificationId: string,
    data: { status: VerificationStatus; notes?: string; rejectionReason?: string }
  ): Promise<VerificationRequest> => {
    return fetchApi<VerificationRequest>(`/admin/verifications/${verificationId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get GDPR dashboard summary
   */
  getGDPRDashboardSummary: async (): Promise<GDPRDashboardSummary> => {
    return fetchApi<GDPRDashboardSummary>('/admin/gdpr/summary');
  },

  /**
   * List GDPR requests
   */
  listGDPRRequests: async (params: {
    status?: GDPRRequestStatus;
    type?: GDPRRequestType;
    page?: number;
    pageSize?: number;
  } = {}): Promise<{ data: GDPRRequest[]; total: number }> => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return fetchApi(`/admin/gdpr/requests${query ? `?${query}` : ''}`);
  },

  /**
   * Get compliance reports
   */
  getComplianceReports: async (): Promise<{ data: ComplianceReport[] }> => {
    return fetchApi('/admin/compliance/reports');
  },

  /**
   * Export audit logs
   */
  exportAuditLogs: async (params: {
    startDate?: string;
    endDate?: string;
    format?: 'csv' | 'json';
  } = {}): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    const response = await fetch(`${API_BASE_URL}/admin/audit-logs/export${query ? `?${query}` : ''}`, {
      credentials: 'include',
    });
    return response.blob();
  },
};

// =============================================================================
// Users API
// =============================================================================

export const usersApi = {
  /**
   * List users with search/filter
   */
  list: async (params: UserListParams = {}): Promise<UserListResponse> => {
    return adminApi.listUsers(params);
  },

  /**
   * Get user by ID
   */
  get: async (userId: string) => {
    return fetchApi(`/admin/users/${userId}`);
  },

  /**
   * Update user status
   */
  updateStatus: async (userId: string, status: string) => {
    return fetchApi(`/admin/users/${userId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  /**
   * Reset user password (admin)
   */
  resetPassword: async (userId: string) => {
    return fetchApi(`/admin/users/${userId}/reset-password`, {
      method: 'POST',
    });
  },

  /**
   * Disable user MFA (admin)
   */
  disableMfa: async (userId: string) => {
    return fetchApi(`/admin/users/${userId}/disable-mfa`, {
      method: 'POST',
    });
  },
};

// =============================================================================
// Tenants API
// =============================================================================

export const tenantsApi = {
  /**
   * List all tenants
   */
  list: async (params: { page?: number; pageSize?: number } = {}): Promise<TenantListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.pageSize) searchParams.set('page_size', String(params.pageSize));
    const query = searchParams.toString();
    return fetchApi<TenantListResponse>(`/admin/tenants${query ? `?${query}` : ''}`);
  },

  /**
   * Get tenant by ID
   */
  get: async (tenantId: string) => {
    return fetchApi(`/admin/tenants/${tenantId}`);
  },

  /**
   * Get tenant statistics
   */
  getStats: async (tenantId: string) => {
    return fetchApi(`/admin/tenants/${tenantId}/stats`);
  },

  /**
   * Update tenant status
   */
  updateStatus: async (tenantId: string, status: string) => {
    return fetchApi(`/admin/tenants/${tenantId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  /**
   * Get tenant detail with data field wrapper
   */
  getDetail: async (tenantId: string): Promise<{ data: TenantListResponse['data'][0] }> => {
    const tenant = await fetchApi<TenantListResponse['data'][0]>(`/admin/tenants/${tenantId}`);
    return { data: tenant };
  },
};

// =============================================================================
// GDPR/Compliance API
// =============================================================================

export const gdprApi = {
  /**
   * Get GDPR dashboard summary
   */
  getDashboardSummary: async (): Promise<GDPRDashboardSummary> => {
    return fetchApi<GDPRDashboardSummary>('/admin/gdpr/summary');
  },

  /**
   * List GDPR requests
   */
  listRequests: async (params: {
    status?: GDPRRequestStatus;
    type?: GDPRRequestType;
    page?: number;
    pageSize?: number;
  } = {}): Promise<{ data: GDPRRequest[]; total: number }> => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return fetchApi(`/admin/gdpr/requests${query ? `?${query}` : ''}`);
  },

  /**
   * Get compliance reports
   */
  getReports: async (): Promise<{ data: ComplianceReport[] }> => {
    return fetchApi('/admin/compliance/reports');
  },

  /**
   * Export audit logs
   */
  exportAuditLogs: async (params: {
    startDate?: string;
    endDate?: string;
    format?: 'csv' | 'json';
  } = {}): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    const response = await fetch(`${API_BASE_URL}/admin/audit-logs/export${query ? `?${query}` : ''}`, {
      credentials: 'include',
    });
    return response.blob();
  },
};

// =============================================================================
// Orders API
// =============================================================================

export const ordersApi = {
  /**
   * List orders
   */
  list: async (params: {
    status?: OrderStatus;
    type?: OrderType;
    page?: number;
    pageSize?: number;
    search?: string;
  } = {}): Promise<{ data: Order[]; total: number }> => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return fetchApi(`/admin/orders${query ? `?${query}` : ''}`);
  },

  /**
   * Get order by ID
   */
  get: async (orderId: string): Promise<Order> => {
    return fetchApi<Order>(`/admin/orders/${orderId}`);
  },

  /**
   * Get order stats
   */
  getStats: async (): Promise<OrderStats> => {
    return fetchApi<OrderStats>('/admin/orders/stats');
  },

  /**
   * Refund order
   */
  refund: async (orderId: string, amount?: number): Promise<Order> => {
    return fetchApi<Order>(`/admin/orders/${orderId}/refund`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  },
};

// =============================================================================
// Support Tickets API
// =============================================================================

export const supportApi = {
  /**
   * List support tickets
   */
  list: async (params: {
    status?: SupportTicketStatus;
    priority?: SupportTicketPriority;
    page?: number;
    pageSize?: number;
    search?: string;
  } = {}): Promise<{ data: SupportTicket[]; total: number }> => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const query = searchParams.toString();
    return fetchApi(`/admin/support/tickets${query ? `?${query}` : ''}`);
  },

  /**
   * Get ticket by ID
   */
  get: async (ticketId: string): Promise<SupportTicket> => {
    return fetchApi<SupportTicket>(`/admin/support/tickets/${ticketId}`);
  },

  /**
   * Get ticket stats
   */
  getStats: async (): Promise<SupportTicketStats> => {
    return fetchApi<SupportTicketStats>('/admin/support/stats');
  },

  /**
   * Reply to ticket
   */
  reply: async (ticketId: string, message: string): Promise<SupportTicketMessage> => {
    return fetchApi<SupportTicketMessage>(`/admin/support/tickets/${ticketId}/reply`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  },

  /**
   * Assign ticket
   */
  assign: async (ticketId: string, userId: string): Promise<SupportTicket> => {
    return fetchApi<SupportTicket>(`/admin/support/tickets/${ticketId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  },

  /**
   * Update ticket status
   */
  updateStatus: async (ticketId: string, status: SupportTicketStatus): Promise<SupportTicket> => {
    return fetchApi<SupportTicket>(`/admin/support/tickets/${ticketId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },
};

// =============================================================================
// AI Admin API (SCR-512, SCR-513)
// =============================================================================

export type CostAlertLevel = 'normal' | 'warning' | 'critical';

export interface CostByProvider {
  provider: string;
  operations: number;
  tokensUsed: number;
  estimatedCostUsd: number;
  percentage: number;
}

export interface CostByOperation {
  operationType: string;
  count: number;
  tokensUsed: number;
  estimatedCostUsd: number;
}

export interface TopUserUsage {
  tenantId: string;
  tenantName: string;
  operations: number;
  tokensUsed: number;
  estimatedCostUsd: number;
}

export interface AICostSummary {
  todayCostUsd: number;
  todayBudgetUsd: number;
  todayUsagePercentage: number;
  monthCostUsd: number;
  monthBudgetUsd: number;
  monthUsagePercentage: number;
  alertLevel: CostAlertLevel;
  alertMessage: string | null;
  byProvider: CostByProvider[];
  byOperation: CostByOperation[];
  topTenants: TopUserUsage[];
  periodStart: string;
  periodEnd: string;
}

export interface CostTrendItem {
  date: string;
  operations: number;
  tokensUsed: number;
  estimatedCostUsd: number;
}

export interface CostTrendResponse {
  days: number;
  trend: CostTrendItem[];
  totalCostUsd: number;
  averageDailyCostUsd: number;
}

export interface CostAlertThreshold {
  warningPercentage: number;
  criticalPercentage: number;
  dailyBudgetUsd: number;
  monthlyBudgetUsd: number;
}

export interface CostExportResponse {
  exportId: string;
  status: string;
  downloadUrl: string | null;
  expiresAt: string | null;
}

export type QueueStatus = 'healthy' | 'degraded' | 'overloaded' | 'stopped';

export interface QueueStats {
  queued: number;
  processing: number;
  completedToday: number;
  failedToday: number;
  averageWaitMs: number;
  averageProcessingMs: number;
}

export interface QueueOverview {
  status: QueueStatus;
  stats: QueueStats;
  activeWorkers: number;
  maxWorkers: number;
  queueDepth: number;
  oldestTaskAgeSeconds: number | null;
}

export interface QueuedTask {
  taskId: string;
  tenantId: string;
  userId: string;
  operationType: string;
  status: string;
  priority: number;
  queuedAt: string;
  startedAt: string | null;
  estimatedCompletion: string | null;
  resourceId: string | null;
  error: string | null;
}

export interface QueueTasksResponse {
  tasks: QueuedTask[];
  total: number;
  page: number;
  pageSize: number;
}

export interface QueueConfig {
  maxQueueSize: number;
  defaultPriority: number;
  maxRetries: number;
  retryDelaySeconds: number;
  timeoutSeconds: number;
  rateLimitPerMinute: number;
}

// =============================================================================
// AI Provider Settings Types (SCR-514)
// =============================================================================

export interface AIProviderInfo {
  type: string;
  name: string;
  status: string;
  latencyMs: number;
  model: string | null;
  error: string | null;
}

export interface AIStatusResponse {
  enabled: boolean;
  provider: AIProviderInfo;
  message: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface AIProvidersStatusResponse {
  currentProvider: string;
  enabled: boolean;
  providers: Record<string, {
    name: string;
    status: string;
    model: string | null;
    reason: string | null;
    error?: string;
  }>;
}

export interface AITestPromptResult {
  success: boolean;
  response?: string;
  error?: string;
  latencyMs?: number;
  tokensUsed?: number;
  provider?: string;
  model?: string;
}

export const aiAdminApi = {
  // ===========================================================================
  // AI Provider Settings (SCR-514: US-048, US-049, US-050)
  // ===========================================================================

  /**
   * Get AI service status (US-048)
   */
  getStatus: async (): Promise<AIStatusResponse> => {
    return fetchApi<AIStatusResponse>('/admin/ai/status');
  },

  /**
   * Get all providers status (US-048)
   */
  getProvidersStatus: async (): Promise<AIProvidersStatusResponse> => {
    return fetchApi<AIProvidersStatusResponse>('/admin/ai/providers');
  },

  /**
   * Toggle AI service on/off (US-049)
   */
  toggleAI: async (data: { enabled: boolean; message?: string }): Promise<AIStatusResponse> => {
    return fetchApi<AIStatusResponse>('/admin/ai/toggle', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Switch AI provider (US-050)
   */
  switchProvider: async (provider: 'openai' | 'ollama' | 'mock'): Promise<AIStatusResponse> => {
    return fetchApi<AIStatusResponse>('/admin/ai/provider', {
      method: 'POST',
      body: JSON.stringify({ provider }),
    });
  },

  /**
   * Test AI provider with a prompt
   */
  testPrompt: async (prompt: string): Promise<AITestPromptResult> => {
    return fetchApi<AITestPromptResult>('/admin/ai/test', {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    });
  },

  // ===========================================================================
  // AI Cost Monitoring (SCR-512)
  // ===========================================================================

  /**
   * Get AI cost summary (SCR-512)
   */
  getCostSummary: async (): Promise<AICostSummary> => {
    return fetchApi<AICostSummary>('/admin/ai/costs');
  },

  /**
   * Get cost trend (SCR-512)
   */
  getCostTrend: async (days: number = 30): Promise<CostTrendResponse> => {
    return fetchApi<CostTrendResponse>(`/admin/ai/costs/trend?days=${days}`);
  },

  /**
   * Get cost alert thresholds (SCR-512)
   */
  getCostThresholds: async (): Promise<CostAlertThreshold> => {
    return fetchApi<CostAlertThreshold>('/admin/ai/costs/thresholds');
  },

  /**
   * Update cost alert thresholds (SCR-512)
   */
  updateCostThresholds: async (thresholds: Partial<CostAlertThreshold>): Promise<CostAlertThreshold> => {
    return fetchApi<CostAlertThreshold>('/admin/ai/costs/thresholds', {
      method: 'PATCH',
      body: JSON.stringify(thresholds),
    });
  },

  /**
   * Export cost report (SCR-512)
   */
  exportCostReport: async (params: {
    startDate: string;
    endDate: string;
    format?: 'csv' | 'json' | 'xlsx';
    includeDetails?: boolean;
  }): Promise<CostExportResponse> => {
    return fetchApi<CostExportResponse>('/admin/ai/costs/export', {
      method: 'POST',
      body: JSON.stringify({
        start_date: params.startDate,
        end_date: params.endDate,
        format: params.format || 'csv',
        include_details: params.includeDetails ?? true,
      }),
    });
  },

  /**
   * Get queue overview (SCR-513)
   */
  getQueueOverview: async (): Promise<QueueOverview> => {
    return fetchApi<QueueOverview>('/admin/ai/queue');
  },

  /**
   * Get queue tasks (SCR-513)
   */
  getQueueTasks: async (params: {
    status?: string;
    tenantId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<QueueTasksResponse> => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.set('status', params.status);
    if (params.tenantId) searchParams.set('tenant_id', params.tenantId);
    if (params.page) searchParams.set('page', String(params.page));
    if (params.pageSize) searchParams.set('page_size', String(params.pageSize));
    const query = searchParams.toString();
    return fetchApi<QueueTasksResponse>(`/admin/ai/queue/tasks${query ? `?${query}` : ''}`);
  },

  /**
   * Get queue config (SCR-513)
   */
  getQueueConfig: async (): Promise<QueueConfig> => {
    return fetchApi<QueueConfig>('/admin/ai/queue/config');
  },

  /**
   * Update queue config (SCR-513)
   */
  updateQueueConfig: async (config: Partial<QueueConfig>): Promise<QueueConfig> => {
    return fetchApi<QueueConfig>('/admin/ai/queue/config', {
      method: 'PATCH',
      body: JSON.stringify(config),
    });
  },

  /**
   * Pause queue (SCR-513)
   */
  pauseQueue: async (): Promise<{ status: string; message: string }> => {
    return fetchApi('/admin/ai/queue/pause', { method: 'POST' });
  },

  /**
   * Resume queue (SCR-513)
   */
  resumeQueue: async (): Promise<{ status: string; message: string }> => {
    return fetchApi('/admin/ai/queue/resume', { method: 'POST' });
  },

  /**
   * Flush queue (SCR-513)
   */
  flushQueue: async (): Promise<{ status: string; message: string; tasksRemoved: number }> => {
    return fetchApi('/admin/ai/queue/flush', { method: 'POST' });
  },

  /**
   * Cancel a task (SCR-513)
   */
  cancelTask: async (taskId: string): Promise<{ taskId: string; cancelled: boolean; message: string }> => {
    return fetchApi(`/admin/ai/queue/tasks/${taskId}`, { method: 'DELETE' });
  },

  /**
   * Bulk cancel tasks (SCR-513)
   */
  bulkCancelTasks: async (taskIds: string[]): Promise<{ cancelled: number; failed: number }> => {
    return fetchApi('/admin/ai/queue/tasks/cancel', {
      method: 'POST',
      body: JSON.stringify({ task_ids: taskIds }),
    });
  },

  /**
   * Update task priority (SCR-513)
   */
  updateTaskPriority: async (taskId: string, priority: number): Promise<QueuedTask> => {
    return fetchApi<QueuedTask>(`/admin/ai/queue/tasks/${taskId}/priority`, {
      method: 'PATCH',
      body: JSON.stringify({ priority }),
    });
  },
};

// =============================================================================
// Access Grants API (JM-002 Document Sharing)
// =============================================================================

export type GrantScope = 'all' | 'category' | 'document';
export type GrantPermission = 'view' | 'download' | 'annotate' | 'share' | 'export';
export type GrantStatus = 'pending' | 'active' | 'expired' | 'revoked' | 'declined' | 'emergency_requested' | 'emergency_denied';
export type GrantRelationship = 'spouse' | 'child' | 'parent' | 'sibling' | 'advisor' | 'accountant' | 'other';

export interface AccessGrantCreate {
  granteeEmail: string;
  relationship: GrantRelationship;
  scope: GrantScope;
  scopeFilter?: {
    categories?: string[];
    documentIds?: string[];
  };
  permissions: GrantPermission[];
  validTo?: string | null;
  invitationMessage?: string | null;
  isEmergencyContact?: boolean;
  emergencyWaitingHours?: number | null;
}

export interface AccessGrantResponse {
  grantId: string;
  grantorUserId: string;
  granteeType: string;
  granteeUserId: string | null;
  granteeEmail: string;
  granteeName?: string;
  relationship: GrantRelationship;
  scope: GrantScope;
  scopeFilter: Record<string, unknown>;
  permissions: GrantPermission[];
  isEmergencyContact: boolean;
  status: GrantStatus;
  validFrom: string;
  validTo: string | null;
  invitationExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  emergencyWaitingHours: number | null;
  emergencyRequestedAt: string | null;
}

export interface AccessGrantListResponse {
  data: AccessGrantResponse[];
  total: number;
  page?: number;
  pageSize?: number;
}

export interface RecentGrantee {
  id: string;
  email: string;
  name: string;
  relationship: GrantRelationship;
  lastSharedAt: string;
  activeGrants: number;
}

export interface GrantActivityLog {
  id: string;
  grantId: string;
  action: string;
  actorUserId: string;
  actorEmail: string;
  details: Record<string, unknown>;
  ipAddress?: string;
  timestamp: string;
}

export const accessGrantsApi = {
  /**
   * Create a new access grant (share document/vault)
   */
  create: async (data: AccessGrantCreate): Promise<AccessGrantResponse> => {
    return fetchApi<AccessGrantResponse>('/access-grants', {
      method: 'POST',
      body: JSON.stringify({
        grantee_email: data.granteeEmail,
        relationship: data.relationship,
        scope: data.scope,
        scope_filter: data.scopeFilter || {},
        permissions: data.permissions,
        valid_to: data.validTo,
        invitation_message: data.invitationMessage,
        is_emergency_contact: data.isEmergencyContact || false,
        emergency_waiting_hours: data.emergencyWaitingHours,
      }),
    });
  },

  /**
   * List grants created by current user
   */
  list: async (params: {
    status?: GrantStatus;
    page?: number;
    pageSize?: number;
  } = {}): Promise<AccessGrantListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.set('status', params.status);
    if (params.page) searchParams.set('page', String(params.page));
    if (params.pageSize) searchParams.set('page_size', String(params.pageSize));
    const query = searchParams.toString();
    return fetchApi<AccessGrantListResponse>(`/access-grants${query ? `?${query}` : ''}`);
  },

  /**
   * Get a specific grant by ID
   */
  get: async (grantId: string): Promise<AccessGrantResponse> => {
    return fetchApi<AccessGrantResponse>(`/access-grants/${grantId}`);
  },

  /**
   * Update a grant (permissions, expiry)
   */
  update: async (grantId: string, data: {
    permissions?: GrantPermission[];
    validTo?: string | null;
    scope?: GrantScope;
    scopeFilter?: Record<string, unknown>;
  }): Promise<AccessGrantResponse> => {
    return fetchApi<AccessGrantResponse>(`/access-grants/${grantId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        permissions: data.permissions,
        valid_to: data.validTo,
        scope: data.scope,
        scope_filter: data.scopeFilter,
      }),
    });
  },

  /**
   * Revoke a grant
   */
  revoke: async (grantId: string, reason?: string): Promise<void> => {
    await fetchApi(`/access-grants/${grantId}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Resend invitation email
   */
  resendInvitation: async (grantId: string): Promise<{ message: string }> => {
    return fetchApi<{ message: string }>(`/access-grants/${grantId}/resend`, {
      method: 'POST',
    });
  },

  /**
   * Get activity log for a grant
   */
  getActivity: async (grantId: string): Promise<{ data: GrantActivityLog[] }> => {
    return fetchApi<{ data: GrantActivityLog[] }>(`/access-grants/${grantId}/activity`);
  },

  /**
   * Get all activity logs across all grants (for owner's activity log page)
   * Connected to GET /api/v1/access-grants/log
   */
  getAllActivities: async (filters?: {
    granteeId?: string;
    documentId?: string;
    action?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: GrantActivityLog[]; total: number }> => {
    const params = new URLSearchParams();
    if (filters?.granteeId) params.set('grantee_id', filters.granteeId);
    if (filters?.documentId) params.set('document_id', filters.documentId);
    if (filters?.action) params.set('action', filters.action);
    if (filters?.dateFrom) params.set('date_from', filters.dateFrom);
    if (filters?.dateTo) params.set('date_to', filters.dateTo);
    if (filters?.limit) params.set('limit', filters.limit.toString());
    if (filters?.offset) params.set('offset', filters.offset.toString());

    const queryString = params.toString();
    const url = queryString ? `/access-grants/log?${queryString}` : '/access-grants/log';
    return fetchApi<{ data: GrantActivityLog[]; total: number }>(url);
  },

  /**
   * Get recent grantees for autocomplete
   */
  getRecentGrantees: async (limit: number = 5): Promise<RecentGrantee[]> => {
    return fetchApi<RecentGrantee[]>(`/access-grants/grantees?limit=${limit}&sort=last_shared`);
  },

  /**
   * Accept an invitation (grantee action)
   */
  acceptInvitation: async (grantId: string): Promise<AccessGrantResponse> => {
    return fetchApi<AccessGrantResponse>(`/access-grants/${grantId}/accept`, {
      method: 'POST',
    });
  },

  /**
   * Decline an invitation (grantee action)
   */
  declineInvitation: async (grantId: string, reason?: string): Promise<void> => {
    await fetchApi(`/access-grants/${grantId}/decline`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Request emergency access (grantee action)
   */
  requestEmergencyAccess: async (grantId: string, reason: string): Promise<AccessGrantResponse> => {
    return fetchApi<AccessGrantResponse>(`/access-grants/${grantId}/emergency-request`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Approve emergency access (grantor action)
   */
  approveEmergencyAccess: async (grantId: string): Promise<AccessGrantResponse> => {
    return fetchApi<AccessGrantResponse>(`/access-grants/${grantId}/emergency-approve`, {
      method: 'POST',
    });
  },

  /**
   * Deny emergency access (grantor action)
   */
  denyEmergencyAccess: async (grantId: string, reason?: string): Promise<AccessGrantResponse> => {
    return fetchApi<AccessGrantResponse>(`/access-grants/${grantId}/emergency-deny`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
};

// =============================================================================
// Generic API Client (for backward compatibility)
// =============================================================================

/**
 * Generic API client with REST methods.
 * Provides a simple interface for making API calls.
 *
 * @example
 * const response = await apiClient.get<MyType>('/endpoint');
 * const data = await apiClient.post<MyType>('/endpoint', { body: data });
 */
export const apiClient = {
  /**
   * Make a GET request
   */
  get: async <T>(endpoint: string, config?: { params?: Record<string, unknown> }): Promise<{ data: T }> => {
    let url = endpoint;
    if (config?.params) {
      const searchParams = new URLSearchParams();
      Object.entries(config.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.set(key, String(value));
        }
      });
      const query = searchParams.toString();
      if (query) {
        url += `?${query}`;
      }
    }
    const data = await fetchApi<T>(url);
    return { data };
  },

  /**
   * Make a POST request
   */
  post: async <T>(endpoint: string, body?: unknown): Promise<{ data: T }> => {
    const data = await fetchApi<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    return { data };
  },

  /**
   * Make a PUT request
   */
  put: async <T>(endpoint: string, body?: unknown): Promise<{ data: T }> => {
    const data = await fetchApi<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
    return { data };
  },

  /**
   * Make a PATCH request
   */
  patch: async <T>(endpoint: string, body?: unknown): Promise<{ data: T }> => {
    const data = await fetchApi<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
    return { data };
  },

  /**
   * Make a DELETE request
   */
  delete: async <T = void>(endpoint: string, config?: { data?: unknown }): Promise<{ data: T }> => {
    const data = await fetchApi<T>(endpoint, {
      method: 'DELETE',
      body: config?.data ? JSON.stringify(config.data) : undefined,
    });
    return { data };
  },
};

// =============================================================================
// Redaction Templates API (BRD-014, GAP-053, GAP-060, GAP-062)
// =============================================================================

/**
 * Pattern testing types - internal to API module
 * These are different from the component prop types, using API-specific naming
 */
interface ApiPatternTestRequest {
  pattern: string;
  testText: string;
  category?: string;
  flags?: string[];
}

interface ApiPatternMatch {
  value: string;
  start: number;
  end: number;
  groups: string[];
}

interface ApiPatternTestResponse {
  valid: boolean;
  error?: string | null;
  matches: ApiPatternMatch[];
  matchCount: number;
  executionTimeMs: number;
  warnings: string[];
  highlightedText?: string | null;
}

interface ApiPatternDefinition {
  id: string;
  name: string;
  pattern: string;
  category: string;
  severity: string;
  enabled: boolean;
  validator?: string | null;
}

interface ApiPatternValidationResult {
  patternId: string;
  valid: boolean;
  error?: string | null;
  warnings: string[];
}

interface ApiPatternValidateResponse {
  allValid: boolean;
  results: ApiPatternValidationResult[];
}

interface ApiBuiltInPatternInfo {
  category: string;
  name: string;
  description: string;
  exampleMatches: string[];
  hasValidator: boolean;
}

interface ApiDocumentTestMatch {
  patternId?: string | null;
  patternName?: string | null;
  category: string;
  value: string;
  start: number;
  end: number;
  confidence: number;
  lineNumber?: number | null;
  context?: string | null;
}

interface ApiDocumentTestRequest {
  documentText: string;
  patterns: ApiPatternDefinition[];
  includeBuiltin: boolean;
  confidenceThreshold: number;
}

interface ApiDocumentTestResponse {
  matches: ApiDocumentTestMatch[];
  matchCount: number;
  matchesByCategory: Record<string, number>;
  matchesByPattern: Record<string, number>;
  coveragePercent: number;
  documentPreview?: string | null;
  executionTimeMs: number;
  warnings: string[];
}

// Re-export component-compatible types for consumers
// Import the component types and re-export as the API response types
import type { PatternMatch, PatternTestResult, BuiltInPattern } from '../components/PatternTester';
import type { DocumentTestMatch, DocumentTestResult, PatternDefinition } from '../components/DocumentTester';
export type { PatternMatch, PatternTestResult, BuiltInPattern };
export type { DocumentTestMatch, DocumentTestResult, PatternDefinition };

/**
 * Redaction Templates API client
 * Provides pattern testing and validation endpoints for template management.
 */
// =============================================================================
// Content Moderation API (SCR-511: BRD-016)
// =============================================================================

export type ModerationAction = 'approve' | 'warn' | 'remove';
export type ModerationStatus = 'pending' | 'urgent' | 'appealed' | 'resolved';

export interface FlagsByReason {
  count: number;
  comments: string[];
}

export interface FlaggerBreakdown {
  consumers: number;
  professionals: number;
}

export interface FlaggedContentAuthor {
  id: string;
  name: string;
  title: string;
  rating: number;
  avatarUrl?: string;
  publishedCount: number;
  warningCount: number;
  removalCount: number;
}

export interface FlaggedContent {
  id: string;
  contentText: { nl: string; en: string };
  contentType: string;
  questionText: { nl: string; en: string };
  flagCount: number;
  isUrgent: boolean;
  flagsByReason: Record<string, FlagsByReason>;
  flaggerBreakdown: FlaggerBreakdown;
  author: FlaggedContentAuthor;
  flaggedAt: string;
  createdAt: string;
  status?: ModerationStatus;
  previousWarnings?: number;
  appealReason?: string;
}

export interface ModerationStats {
  pendingCount: number;
  urgentCount: number;
  appealedCount: number;
  resolvedThisWeek: number;
}

export interface FlaggedContentQueueResponse {
  items: FlaggedContent[];
  stats: ModerationStats;
  totalCount: number;
}

export interface ModerationRequest {
  action: ModerationAction;
  reason?: string;
}

export interface ModerationResponse {
  contentId: string;
  action: string;
  ratingImpact: number | null;
  newStatus: string;
  moderatedAt: string;
}

export const contentModerationApi = {
  /**
   * Get flagged content queue (SCR-511)
   */
  getFlaggedContent: async (params?: {
    status?: ModerationStatus;
    sortBy?: 'newest' | 'oldest' | 'mostFlags';
    page?: number;
    pageSize?: number;
  }): Promise<FlaggedContentQueueResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.sortBy) searchParams.set('sort_by', params.sortBy);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.pageSize) searchParams.set('page_size', String(params.pageSize));
    const query = searchParams.toString();
    return fetchApi<FlaggedContentQueueResponse>(
      `/admin/prompt-builder/expertise-content/flagged${query ? `?${query}` : ''}`
    );
  },

  /**
   * Moderate flagged content (SCR-511)
   */
  moderateContent: async (
    contentId: string,
    data: ModerationRequest
  ): Promise<ModerationResponse> => {
    return fetchApi<ModerationResponse>(
      `/admin/prompt-builder/expertise-content/${contentId}/moderate`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },
};

// =============================================================================
// Redaction Templates API (BRD-014, GAP-053, GAP-060, GAP-062)
// =============================================================================

export const redactionTemplatesApi = {
  /**
   * Test a regex pattern against sample text (GAP-053, GAP-062)
   *
   * Uses the backend PIIDetector for accurate validation including
   * built-in validators (BSN 11-proof, IBAN mod-97, etc.)
   */
  testPattern: async (data: ApiPatternTestRequest): Promise<ApiPatternTestResponse> => {
    return fetchApi<ApiPatternTestResponse>('/redaction/templates/patterns/test', {
      method: 'POST',
      body: JSON.stringify({
        pattern: data.pattern,
        test_text: data.testText,
        category: data.category,
        flags: data.flags || [],
      }),
    });
  },

  /**
   * Validate multiple patterns at once (GAP-053, GAP-062)
   *
   * Checks regex syntax validity and provides performance warnings.
   */
  validatePatterns: async (patterns: ApiPatternDefinition[]): Promise<ApiPatternValidateResponse> => {
    return fetchApi<ApiPatternValidateResponse>('/redaction/templates/patterns/validate', {
      method: 'POST',
      body: JSON.stringify({
        patterns: patterns.map(p => ({
          id: p.id,
          name: p.name,
          pattern: p.pattern,
          category: p.category,
          severity: p.severity,
          enabled: p.enabled,
          validator: p.validator,
        })),
      }),
    });
  },

  /**
   * List all built-in PII patterns (GAP-053, GAP-062)
   *
   * Returns available patterns like BSN, IBAN, email, phone, etc.
   */
  listBuiltinPatterns: async (): Promise<ApiBuiltInPatternInfo[]> => {
    return fetchApi<ApiBuiltInPatternInfo[]>('/redaction/templates/patterns/builtin');
  },

  /**
   * Test template patterns against a sample document (GAP-060, GAP-062)
   *
   * Applies both custom patterns and built-in detectors to find all PII.
   */
  testDocument: async (data: ApiDocumentTestRequest): Promise<ApiDocumentTestResponse> => {
    return fetchApi<ApiDocumentTestResponse>('/redaction/templates/test-document', {
      method: 'POST',
      body: JSON.stringify({
        document_text: data.documentText,
        patterns: data.patterns.map(p => ({
          id: p.id,
          name: p.name,
          pattern: p.pattern,
          category: p.category,
          severity: p.severity,
          enabled: p.enabled,
          validator: p.validator,
        })),
        include_builtin: data.includeBuiltin,
        confidence_threshold: data.confidenceThreshold,
      }),
    });
  },
};

// =============================================================================
// Test Data API (BRD-017)
// =============================================================================

export type TestDataEnvironment = 'dev' | 'test' | 'staging' | 'prod_like';
export type TestDataEntityType =
  | 'tenants'
  | 'users'
  | 'documents'
  | 'professional_profiles'
  | 'access_grants'
  | 'subscriptions'
  | 'ai_features'
  | 'marketplace'
  | 'encryption'
  | 'audit_logs'
  | 'notifications';

export type GenerationJobStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface TestDataVolumeConfig {
  tenants: number;
  usersPerTenant: number;
  documentsPerUser: number;
  accessGrants: number;
}

export interface TestDataEntityConfig {
  type: TestDataEntityType;
  enabled: boolean;
  count?: number;
}

export interface EnvironmentProfile {
  id: TestDataEnvironment;
  name: string;
  description: string;
  defaultVolume: TestDataVolumeConfig;
  estimatedRecords: number;
  estimatedTime: string;
}

export interface EntityPreview {
  type: TestDataEntityType;
  count: number;
  dependencies: TestDataEntityType[];
}

export interface StorageEstimate {
  database: number;
  minio: number;
}

export interface GenerationPreview {
  environment: TestDataEnvironment;
  volume: TestDataVolumeConfig;
  entities: TestDataEntityConfig[];
  preview: EntityPreview[];
  estimatedTime: number;
  estimatedStorage: StorageEstimate;
  warnings: string[];
}

export interface GenerationJob {
  id: string;
  environment: TestDataEnvironment;
  status: GenerationJobStatus;
  progressPercent: number;
  currentEntity?: TestDataEntityType;
  recordsCreated: Record<string, number>;
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
  createdAt: string;
}

export interface TestDataProfile {
  id: string;
  name: string;
  description?: string;
  environment: TestDataEnvironment;
  volume: TestDataVolumeConfig;
  entities: TestDataEntityConfig[];
  isDefault: boolean;
  usageCount: number;
  lastUsedAt?: string;
  createdAt: string;
}

export interface GenerationJobListResponse {
  items: GenerationJob[];
  total: number;
}

export interface TestDataProfileListResponse {
  items: TestDataProfile[];
  total: number;
}

export interface ClearTestDataResponse {
  cleared: number;
  recordsDeleted: Record<string, number>;
}

export const testDataApi = {
  /**
   * Get available environment profiles
   */
  getEnvironments: async (): Promise<EnvironmentProfile[]> => {
    return fetchApi<EnvironmentProfile[]>('/admin/test-data/environments');
  },

  /**
   * Get a specific environment profile
   */
  getEnvironment: async (environment: TestDataEnvironment): Promise<EnvironmentProfile> => {
    return fetchApi<EnvironmentProfile>(`/admin/test-data/environments/${environment}`);
  },

  /**
   * Preview what data will be generated
   */
  preview: async (data: {
    environment: TestDataEnvironment;
    volume: TestDataVolumeConfig;
    entities: TestDataEntityConfig[];
  }): Promise<GenerationPreview> => {
    return fetchApi<GenerationPreview>('/admin/test-data/preview', {
      method: 'POST',
      body: JSON.stringify({
        environment: data.environment,
        volume: {
          tenants: data.volume.tenants,
          users_per_tenant: data.volume.usersPerTenant,
          documents_per_user: data.volume.documentsPerUser,
          access_grants: data.volume.accessGrants,
        },
        entities: data.entities.map(e => ({
          type: e.type,
          enabled: e.enabled,
          count: e.count,
        })),
      }),
    });
  },

  /**
   * Start data generation job
   */
  generate: async (data: {
    environment: TestDataEnvironment;
    volume: TestDataVolumeConfig;
    entities: TestDataEntityConfig[];
    profileId?: string;
  }): Promise<GenerationJob> => {
    return fetchApi<GenerationJob>('/admin/test-data/generate', {
      method: 'POST',
      body: JSON.stringify({
        environment: data.environment,
        volume: {
          tenants: data.volume.tenants,
          users_per_tenant: data.volume.usersPerTenant,
          documents_per_user: data.volume.documentsPerUser,
          access_grants: data.volume.accessGrants,
        },
        entities: data.entities.map(e => ({
          type: e.type,
          enabled: e.enabled,
          count: e.count,
        })),
        profile_id: data.profileId,
      }),
    });
  },

  /**
   * Get job status
   */
  getJobStatus: async (jobId: string): Promise<GenerationJob> => {
    return fetchApi<GenerationJob>(`/admin/test-data/generate/${jobId}/status`);
  },

  /**
   * Cancel a running job
   */
  cancelJob: async (jobId: string): Promise<GenerationJob> => {
    return fetchApi<GenerationJob>(`/admin/test-data/generate/${jobId}/cancel`, {
      method: 'POST',
    });
  },

  /**
   * List all jobs
   */
  listJobs: async (params?: { limit?: number; offset?: number }): Promise<GenerationJobListResponse> => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    const queryStr = query.toString();
    return fetchApi<GenerationJobListResponse>(`/admin/test-data/jobs${queryStr ? `?${queryStr}` : ''}`);
  },

  /**
   * Clear test data
   */
  clearData: async (entities?: TestDataEntityType[], clearAll?: boolean): Promise<ClearTestDataResponse> => {
    return fetchApi<ClearTestDataResponse>('/admin/test-data', {
      method: 'DELETE',
      body: JSON.stringify({
        entities: entities,
        clear_all: clearAll ?? false,
      }),
    });
  },

  /**
   * List saved profiles
   */
  getProfiles: async (params?: { limit?: number; offset?: number }): Promise<TestDataProfileListResponse> => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    const queryStr = query.toString();
    return fetchApi<TestDataProfileListResponse>(`/admin/test-data/profiles${queryStr ? `?${queryStr}` : ''}`);
  },

  /**
   * Get a profile by ID
   */
  getProfile: async (profileId: string): Promise<TestDataProfile> => {
    return fetchApi<TestDataProfile>(`/admin/test-data/profiles/${profileId}`);
  },

  /**
   * Create a new profile
   */
  createProfile: async (data: {
    name: string;
    description?: string;
    environment: TestDataEnvironment;
    volume: TestDataVolumeConfig;
    entities: TestDataEntityConfig[];
    isDefault?: boolean;
  }): Promise<TestDataProfile> => {
    return fetchApi<TestDataProfile>('/admin/test-data/profiles', {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        description: data.description,
        environment: data.environment,
        volume: {
          tenants: data.volume.tenants,
          users_per_tenant: data.volume.usersPerTenant,
          documents_per_user: data.volume.documentsPerUser,
          access_grants: data.volume.accessGrants,
        },
        entities: data.entities.map(e => ({
          type: e.type,
          enabled: e.enabled,
          count: e.count,
        })),
        is_default: data.isDefault,
      }),
    });
  },

  /**
   * Update a profile
   */
  updateProfile: async (profileId: string, data: Partial<{
    name: string;
    description: string;
    environment: TestDataEnvironment;
    volume: TestDataVolumeConfig;
    entities: TestDataEntityConfig[];
    isDefault: boolean;
  }>): Promise<TestDataProfile> => {
    const body: Record<string, unknown> = {};
    if (data.name !== undefined) body.name = data.name;
    if (data.description !== undefined) body.description = data.description;
    if (data.environment !== undefined) body.environment = data.environment;
    if (data.volume !== undefined) body.volume = {
      tenants: data.volume.tenants,
      users_per_tenant: data.volume.usersPerTenant,
      documents_per_user: data.volume.documentsPerUser,
      access_grants: data.volume.accessGrants,
    };
    if (data.entities !== undefined) body.entities = data.entities.map(e => ({
      type: e.type,
      enabled: e.enabled,
      count: e.count,
    }));
    if (data.isDefault !== undefined) body.is_default = data.isDefault;

    return fetchApi<TestDataProfile>(`/admin/test-data/profiles/${profileId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  /**
   * Delete a profile
   */
  deleteProfile: async (profileId: string): Promise<{ deleted: boolean }> => {
    return fetchApi<{ deleted: boolean }>(`/admin/test-data/profiles/${profileId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Import a profile from JSON
   */
  importProfile: async (data: {
    name: string;
    description?: string;
    environment: TestDataEnvironment;
    volume: TestDataVolumeConfig;
    entities: TestDataEntityConfig[];
  }): Promise<TestDataProfile> => {
    return fetchApi<TestDataProfile>('/admin/test-data/profiles/import', {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        description: data.description,
        environment: data.environment,
        volume: {
          tenants: data.volume.tenants,
          users_per_tenant: data.volume.usersPerTenant,
          documents_per_user: data.volume.documentsPerUser,
          access_grants: data.volume.accessGrants,
        },
        entities: data.entities.map(e => ({
          type: e.type,
          enabled: e.enabled,
          count: e.count,
        })),
      }),
    });
  },

  /**
   * Export a profile as JSON
   */
  exportProfile: async (profileId: string): Promise<{
    name: string;
    description?: string;
    environment: TestDataEnvironment;
    volume: TestDataVolumeConfig;
    entities: TestDataEntityConfig[];
  }> => {
    return fetchApi(`/admin/test-data/profiles/${profileId}/export`);
  },
};

// =============================================================================
// Questionnaire API (BRD-018)
// =============================================================================

export type QuestionnaireStatus = 'draft' | 'review' | 'active' | 'archived';
export type QuestionnaireType = 'life_event' | 'intake' | 'qualification' | 'survey';
export type QuestionType = 'text' | 'textarea' | 'number' | 'range' | 'select' | 'multiselect' | 'boolean' | 'date' | 'file' | 'info';

export interface QuestionOption {
  id: string;
  value: string;
  labelNl: string;
  labelEn: string;
  order: number;
}

export interface ValidationRules {
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  step?: number;
  pattern?: string;
  minSelect?: number;
  maxSelect?: number;
  maxFileSize?: number;
  acceptedFileTypes?: string[];
}

export interface ConditionalLogic {
  operator: 'and' | 'or';
  conditions: Array<{
    questionId: string;
    operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than' | 'is_empty' | 'is_not_empty';
    value: string | number | boolean;
  }>;
}

export interface Question {
  id: string;
  questionnaireId: string;
  sectionId: string | null;
  code: string;
  questionType: QuestionType;
  labelNl: string;
  labelEn: string;
  helpTextNl: string | null;
  helpTextEn: string | null;
  placeholderNl: string | null;
  placeholderEn: string | null;
  required: boolean;
  order: number;
  validationRules: ValidationRules | null;
  options: QuestionOption[] | null;
  conditionalLogic: ConditionalLogic | null;
  createdAt: string;
  updatedAt: string;
}

export interface Section {
  id: string;
  questionnaireId: string;
  titleNl: string;
  titleEn: string;
  descriptionNl: string | null;
  descriptionEn: string | null;
  order: number;
  questions: Question[];
  createdAt: string;
  updatedAt: string;
}

export interface Questionnaire {
  id: string;
  tenantId: string;
  code: string;
  titleNl: string;
  titleEn: string;
  descriptionNl: string | null;
  descriptionEn: string | null;
  questionnaireType: QuestionnaireType;
  status: QuestionnaireStatus;
  version: number;
  supportedLanguages: string[];
  questionCount: number;
  sectionCount: number;
  sections: Section[];
  createdBy: string;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface QuestionnaireListItem {
  id: string;
  tenantId: string;
  code: string;
  titleNl: string;
  titleEn: string;
  descriptionNl: string | null;
  questionnaireType: QuestionnaireType;
  status: QuestionnaireStatus;
  version: number;
  questionCount: number;
  createdBy: string;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface QuestionnaireListParams {
  page?: number;
  limit?: number;
  status?: QuestionnaireStatus;
  type?: QuestionnaireType;
  search?: string;
  sortBy?: 'code' | 'title' | 'status' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

export interface QuestionnaireListResponse {
  items: QuestionnaireListItem[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface QuestionnaireTemplate {
  id: string;
  titleNl: string;
  titleEn: string;
  descriptionNl: string;
  descriptionEn: string;
  questionnaireType: QuestionnaireType;
  questionCount: number;
  sectionCount: number;
  supportedLanguages: string[];
  icon: string;
  source: 'system' | 'user';
  rating?: number;
  ratingCount?: number;
  createdAt: string;
}

export interface QuestionnaireVersion {
  id: string;
  questionnaireId: string;
  version: number;
  publishedAt: string;
  publishedBy: string;
  publishedByName: string;
  changeNotes: string | null;
  changes: {
    added: number;
    modified: number;
    removed: number;
  };
}

export interface QuestionnaireAnalytics {
  summary: {
    started: number;
    completed: number;
    completionRate: number;
    avgDuration: number;
    dropOffRate: number;
  };
  trends: {
    startedChange: number;
    completedChange: number;
    durationChange: number;
    dropOffChange: number;
  };
}

export interface QuestionnaireFunnelStep {
  name: string;
  code: string;
  questionId: string | null;
  value: number;
  percentage: number;
  dropOff: number;
}

export interface QuestionnaireDropOff {
  questionId: string;
  code: string;
  labelNl: string;
  labelEn: string;
  dropOffRate: number;
  dropOffCount: number;
}

// Response types
export interface ResponseStartRequest {
  questionnaireId: string;
  language: 'nl' | 'en';
  sessionToken?: string;
}

export interface ResponseStartResponse {
  responseId: string;
  sessionToken: string;
  questionnaire: Questionnaire;
  savedAnswers: Record<string, unknown>;
  currentStep: number;
}

export interface AnswerSubmitRequest {
  questionId: string;
  value: unknown;
}

export interface ResponseCompleteRequest {
  completedAt?: string;
}

export const questionnaireApi = {
  /**
   * List questionnaires with pagination and filters
   */
  list: async (params: QuestionnaireListParams = {}): Promise<QuestionnaireListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.status) searchParams.set('status', params.status);
    if (params.type) searchParams.set('type', params.type);
    if (params.search) searchParams.set('search', params.search);
    if (params.sortBy) searchParams.set('sort_by', params.sortBy);
    if (params.sortOrder) searchParams.set('sort_order', params.sortOrder);
    const query = searchParams.toString();
    return fetchApi<QuestionnaireListResponse>(`/questionnaires${query ? `?${query}` : ''}`);
  },

  /**
   * Get questionnaire by ID
   */
  get: async (id: string): Promise<Questionnaire> => {
    return fetchApi<Questionnaire>(`/questionnaires/${id}`);
  },

  /**
   * Create a new questionnaire
   */
  create: async (data: {
    code: string;
    titleNl: string;
    titleEn: string;
    descriptionNl?: string;
    descriptionEn?: string;
    questionnaireType: QuestionnaireType;
    supportedLanguages?: string[];
  }): Promise<Questionnaire> => {
    return fetchApi<Questionnaire>('/questionnaires', {
      method: 'POST',
      body: JSON.stringify({
        code: data.code,
        title_nl: data.titleNl,
        title_en: data.titleEn,
        description_nl: data.descriptionNl,
        description_en: data.descriptionEn,
        questionnaire_type: data.questionnaireType,
        supported_languages: data.supportedLanguages || ['nl', 'en'],
      }),
    });
  },

  /**
   * Update questionnaire
   */
  update: async (id: string, data: Partial<{
    titleNl: string;
    titleEn: string;
    descriptionNl: string;
    descriptionEn: string;
    status: QuestionnaireStatus;
    supportedLanguages: string[];
  }>): Promise<Questionnaire> => {
    const body: Record<string, unknown> = {};
    if (data.titleNl !== undefined) body.title_nl = data.titleNl;
    if (data.titleEn !== undefined) body.title_en = data.titleEn;
    if (data.descriptionNl !== undefined) body.description_nl = data.descriptionNl;
    if (data.descriptionEn !== undefined) body.description_en = data.descriptionEn;
    if (data.status !== undefined) body.status = data.status;
    if (data.supportedLanguages !== undefined) body.supported_languages = data.supportedLanguages;

    return fetchApi<Questionnaire>(`/questionnaires/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  /**
   * Delete questionnaire
   */
  delete: async (id: string): Promise<void> => {
    await fetchApi(`/questionnaires/${id}`, { method: 'DELETE' });
  },

  /**
   * Publish questionnaire
   */
  publish: async (id: string, changeNotes?: string): Promise<Questionnaire> => {
    return fetchApi<Questionnaire>(`/questionnaires/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify({ change_notes: changeNotes }),
    });
  },

  /**
   * Add question to questionnaire
   */
  addQuestion: async (questionnaireId: string, data: {
    code: string;
    questionType: QuestionType;
    labelNl: string;
    labelEn: string;
    helpTextNl?: string;
    helpTextEn?: string;
    placeholderNl?: string;
    placeholderEn?: string;
    required?: boolean;
    order?: number;
    sectionId?: string;
    validationRules?: ValidationRules;
    options?: Array<{ value: string; labelNl: string; labelEn: string }>;
    conditionalLogic?: ConditionalLogic;
  }): Promise<Question> => {
    return fetchApi<Question>(`/questionnaires/${questionnaireId}/questions`, {
      method: 'POST',
      body: JSON.stringify({
        code: data.code,
        question_type: data.questionType,
        label_nl: data.labelNl,
        label_en: data.labelEn,
        help_text_nl: data.helpTextNl,
        help_text_en: data.helpTextEn,
        placeholder_nl: data.placeholderNl,
        placeholder_en: data.placeholderEn,
        required: data.required ?? false,
        order: data.order,
        section_id: data.sectionId,
        validation_rules: data.validationRules,
        options: data.options?.map((o, i) => ({
          value: o.value,
          label_nl: o.labelNl,
          label_en: o.labelEn,
          order: i,
        })),
        conditional_logic: data.conditionalLogic,
      }),
    });
  },

  /**
   * Update question
   */
  updateQuestion: async (questionnaireId: string, questionId: string, data: Partial<{
    code: string;
    questionType: QuestionType;
    labelNl: string;
    labelEn: string;
    helpTextNl: string;
    helpTextEn: string;
    placeholderNl: string;
    placeholderEn: string;
    required: boolean;
    order: number;
    sectionId: string;
    validationRules: ValidationRules;
    options: Array<{ value: string; labelNl: string; labelEn: string }>;
    conditionalLogic: ConditionalLogic;
  }>): Promise<Question> => {
    const body: Record<string, unknown> = {};
    if (data.code !== undefined) body.code = data.code;
    if (data.questionType !== undefined) body.question_type = data.questionType;
    if (data.labelNl !== undefined) body.label_nl = data.labelNl;
    if (data.labelEn !== undefined) body.label_en = data.labelEn;
    if (data.helpTextNl !== undefined) body.help_text_nl = data.helpTextNl;
    if (data.helpTextEn !== undefined) body.help_text_en = data.helpTextEn;
    if (data.placeholderNl !== undefined) body.placeholder_nl = data.placeholderNl;
    if (data.placeholderEn !== undefined) body.placeholder_en = data.placeholderEn;
    if (data.required !== undefined) body.required = data.required;
    if (data.order !== undefined) body.order = data.order;
    if (data.sectionId !== undefined) body.section_id = data.sectionId;
    if (data.validationRules !== undefined) body.validation_rules = data.validationRules;
    if (data.options !== undefined) body.options = data.options.map((o, i) => ({
      value: o.value,
      label_nl: o.labelNl,
      label_en: o.labelEn,
      order: i,
    }));
    if (data.conditionalLogic !== undefined) body.conditional_logic = data.conditionalLogic;

    return fetchApi<Question>(`/questionnaires/${questionnaireId}/questions/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  /**
   * Delete question
   */
  deleteQuestion: async (questionnaireId: string, questionId: string): Promise<void> => {
    await fetchApi(`/questionnaires/${questionnaireId}/questions/${questionId}`, { method: 'DELETE' });
  },

  /**
   * Get templates
   */
  getTemplates: async (params?: {
    search?: string;
    category?: QuestionnaireType;
    source?: 'system' | 'user';
  }): Promise<{ systemTemplates: QuestionnaireTemplate[]; userTemplates: QuestionnaireTemplate[] }> => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.category) searchParams.set('category', params.category);
    if (params?.source) searchParams.set('source', params.source);
    const query = searchParams.toString();
    return fetchApi(`/questionnaires/templates${query ? `?${query}` : ''}`);
  },

  /**
   * Create from template
   */
  createFromTemplate: async (templateId: string, data: {
    code: string;
    titleNl?: string;
    titleEn?: string;
  }): Promise<Questionnaire> => {
    return fetchApi<Questionnaire>(`/questionnaires/from-template/${templateId}`, {
      method: 'POST',
      body: JSON.stringify({
        code: data.code,
        title_nl: data.titleNl,
        title_en: data.titleEn,
      }),
    });
  },

  /**
   * Save as template
   */
  saveAsTemplate: async (questionnaireId: string, data: {
    titleNl: string;
    titleEn: string;
    descriptionNl: string;
    descriptionEn: string;
  }): Promise<QuestionnaireTemplate> => {
    return fetchApi<QuestionnaireTemplate>('/questionnaires/templates', {
      method: 'POST',
      body: JSON.stringify({
        questionnaire_id: questionnaireId,
        title_nl: data.titleNl,
        title_en: data.titleEn,
        description_nl: data.descriptionNl,
        description_en: data.descriptionEn,
      }),
    });
  },

  /**
   * Get version history
   */
  getVersions: async (id: string): Promise<{ versions: QuestionnaireVersion[]; currentVersion: number }> => {
    return fetchApi(`/questionnaires/${id}/versions`);
  },

  /**
   * Restore version
   */
  restoreVersion: async (id: string, version: number): Promise<Questionnaire> => {
    return fetchApi<Questionnaire>(`/questionnaires/${id}/versions/${version}/restore`, {
      method: 'POST',
    });
  },

  /**
   * Get analytics
   */
  getAnalytics: async (id: string, params?: {
    from?: string;
    to?: string;
    version?: number;
  }): Promise<QuestionnaireAnalytics> => {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.version) searchParams.set('version', String(params.version));
    const query = searchParams.toString();
    return fetchApi(`/questionnaires/${id}/analytics${query ? `?${query}` : ''}`);
  },

  /**
   * Get funnel data
   */
  getFunnel: async (id: string, params?: {
    from?: string;
    to?: string;
  }): Promise<{ steps: QuestionnaireFunnelStep[] }> => {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    const query = searchParams.toString();
    return fetchApi(`/questionnaires/${id}/analytics/funnel${query ? `?${query}` : ''}`);
  },

  /**
   * Get drop-off data
   */
  getDropOff: async (id: string): Promise<{ questions: QuestionnaireDropOff[] }> => {
    return fetchApi(`/questionnaires/${id}/analytics/drop-off`);
  },
};

// =============================================================================
// Advisor Profile API (BRD-019)
// =============================================================================

export type AdvisorSpecialization =
  | 'insurance'
  | 'mortgage'
  | 'tax'
  | 'pension'
  | 'legal'
  | 'accounting'
  | 'estate'
  | 'hr_benefits'
  | 'investment';

export type AdvisorServiceType =
  | 'document_review'
  | 'consultation'
  | 'ongoing_advisory'
  | 'quick_question'
  | 'product_comparison';

export type AdvisorVerificationStatus = 'pending' | 'verified' | 'suspended' | 'revoked';

export type AdvisorLicenseType = 'AFM' | 'NBA' | 'KNB' | 'NOvA' | 'Wft';

export interface AdvisorLicense {
  type: AdvisorLicenseType;
  number: string;
  validUntil: string | null;
  modules: string[] | null;
  verifiedAt: string | null;
}

export interface AdvisorLicenseCreate {
  type: AdvisorLicenseType;
  number: string;
  validUntil?: string | null;
  modules?: string[] | null;
}

export interface AdvisorProfile {
  advisorId: string;
  tenantId: string;
  userId: string;
  displayName: string;
  organizationName: string | null;
  kvkNumber: string | null;
  specializations: AdvisorSpecialization[];
  bio: string | null;
  websiteUrl: string | null;
  phone: string | null;
  marketplaceVisible: boolean;
  hourlyRateCents: number | null;
  serviceTypes: AdvisorServiceType[];
  licenses: AdvisorLicense[];
  verificationStatus: AdvisorVerificationStatus;
  verifiedAt: string | null;
  liabilityInsured: boolean;
  insuranceVerifiedAt: string | null;
  insuranceValidUntil: string | null;
  avatarUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdvisorProfileCreate {
  displayName: string;
  organizationName?: string | null;
  kvkNumber?: string | null;
  specializations: AdvisorSpecialization[];
  bio?: string | null;
  websiteUrl?: string | null;
  phone?: string | null;
  marketplaceVisible?: boolean;
  hourlyRateCents?: number | null;
  serviceTypes?: AdvisorServiceType[];
  licenses?: AdvisorLicenseCreate[];
}

export interface AdvisorProfileUpdate {
  displayName?: string;
  organizationName?: string | null;
  specializations?: AdvisorSpecialization[];
  bio?: string | null;
  websiteUrl?: string | null;
  phone?: string | null;
  marketplaceVisible?: boolean;
  hourlyRateCents?: number | null;
  serviceTypes?: AdvisorServiceType[];
}

export interface AdvisorProfileListItem {
  advisorId: string;
  displayName: string;
  organizationName: string | null;
  specializations: AdvisorSpecialization[];
  verificationStatus: AdvisorVerificationStatus;
  marketplaceVisible: boolean;
}

export interface AdvisorSearchParams {
  specialization?: AdvisorSpecialization;
  verificationStatus?: AdvisorVerificationStatus;
  marketplaceVisible?: boolean;
  limit?: number;
  offset?: number;
}

export interface AdvisorSearchResponse {
  items: AdvisorProfileListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdvisorVerifyRequest {
  status: 'verified' | 'suspended' | 'revoked';
  notes?: string;
}

export interface AdvisorVerifyResponse {
  advisorId: string;
  verificationStatus: AdvisorVerificationStatus;
  verifiedAt: string | null;
  verifiedBy: string | null;
  verificationNotes: string | null;
}

export const advisorApi = {
  /**
   * Create advisor profile
   */
  createProfile: async (data: AdvisorProfileCreate): Promise<AdvisorProfile> => {
    return fetchApi<AdvisorProfile>('/advisor-profiles', {
      method: 'POST',
      body: JSON.stringify({
        display_name: data.displayName,
        organization_name: data.organizationName,
        kvk_number: data.kvkNumber,
        specializations: data.specializations,
        bio: data.bio,
        website_url: data.websiteUrl,
        phone: data.phone,
        marketplace_visible: data.marketplaceVisible ?? false,
        hourly_rate_cents: data.hourlyRateCents,
        service_types: data.serviceTypes ?? [],
        licenses: data.licenses?.map(l => ({
          type: l.type,
          number: l.number,
          valid_until: l.validUntil,
          modules: l.modules,
        })) ?? [],
      }),
    });
  },

  /**
   * Get current user's advisor profile
   */
  getMyProfile: async (): Promise<AdvisorProfile> => {
    return fetchApi<AdvisorProfile>('/advisor-profiles/me');
  },

  /**
   * Get advisor profile by ID
   */
  getProfile: async (advisorId: string): Promise<AdvisorProfile> => {
    return fetchApi<AdvisorProfile>(`/advisor-profiles/${advisorId}`);
  },

  /**
   * Update advisor profile
   */
  updateProfile: async (advisorId: string, data: AdvisorProfileUpdate): Promise<AdvisorProfile> => {
    const body: Record<string, unknown> = {};
    if (data.displayName !== undefined) body.display_name = data.displayName;
    if (data.organizationName !== undefined) body.organization_name = data.organizationName;
    if (data.specializations !== undefined) body.specializations = data.specializations;
    if (data.bio !== undefined) body.bio = data.bio;
    if (data.websiteUrl !== undefined) body.website_url = data.websiteUrl;
    if (data.phone !== undefined) body.phone = data.phone;
    if (data.marketplaceVisible !== undefined) body.marketplace_visible = data.marketplaceVisible;
    if (data.hourlyRateCents !== undefined) body.hourly_rate_cents = data.hourlyRateCents;
    if (data.serviceTypes !== undefined) body.service_types = data.serviceTypes;

    return fetchApi<AdvisorProfile>(`/advisor-profiles/${advisorId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  /**
   * Add license to advisor profile
   */
  addLicense: async (advisorId: string, license: AdvisorLicenseCreate): Promise<AdvisorProfile> => {
    return fetchApi<AdvisorProfile>(`/advisor-profiles/${advisorId}/licenses`, {
      method: 'POST',
      body: JSON.stringify({
        type: license.type,
        number: license.number,
        valid_until: license.validUntil,
        modules: license.modules,
      }),
    });
  },

  /**
   * Search advisor profiles
   */
  search: async (params: AdvisorSearchParams = {}): Promise<AdvisorSearchResponse> => {
    const searchParams = new URLSearchParams();
    if (params.specialization) searchParams.set('specialization', params.specialization);
    if (params.verificationStatus) searchParams.set('verification_status', params.verificationStatus);
    if (params.marketplaceVisible !== undefined) searchParams.set('marketplace_visible', String(params.marketplaceVisible));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return fetchApi<AdvisorSearchResponse>(`/advisor-profiles${query ? `?${query}` : ''}`);
  },

  /**
   * Browse marketplace advisors
   */
  browseMarketplace: async (params: {
    specialization?: AdvisorSpecialization;
    limit?: number;
    offset?: number;
  } = {}): Promise<AdvisorSearchResponse> => {
    const searchParams = new URLSearchParams();
    if (params.specialization) searchParams.set('specialization', params.specialization);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return fetchApi<AdvisorSearchResponse>(`/advisor-profiles/marketplace${query ? `?${query}` : ''}`);
  },

  /**
   * Verify advisor profile (admin only)
   */
  verifyProfile: async (advisorId: string, data: AdvisorVerifyRequest): Promise<AdvisorVerifyResponse> => {
    return fetchApi<AdvisorVerifyResponse>(`/advisor-profiles/${advisorId}/verify`, {
      method: 'POST',
      body: JSON.stringify({
        status: data.status,
        notes: data.notes,
      }),
    });
  },
};

// =============================================================================
// CRM API (BRD-019)
// =============================================================================

export type CRMClientStatus = 'prospect' | 'active' | 'inactive' | 'churned';
export type CRMCaseStatus = 'open' | 'in_progress' | 'waiting_client' | 'waiting_external' | 'completed' | 'cancelled';
export type CRMCasePriority = 'low' | 'normal' | 'high' | 'urgent';
export type CRMOpportunityStage = 'new' | 'contacted' | 'qualified' | 'proposal' | 'negotiation' | 'won' | 'lost';
export type CRMTaskType = 'call' | 'email' | 'meeting' | 'review' | 'follow_up' | 'deadline' | 'general';
export type CRMTaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type CRMTaskStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'deferred';
export type CRMActivityType = 'call' | 'email_sent' | 'email_received' | 'meeting' | 'note' | 'document_shared' | 'document_reviewed' | 'status_change' | 'system';

// CRM Client Types
export interface CRMClient {
  clientId: string;
  tenantId: string;
  advisorId: string;
  userId: string | null;
  email: string;
  displayName: string;
  phone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  country: string;
  status: CRMClientStatus;
  source: string | null;
  sourceDetails: Record<string, unknown>;
  tags: string[];
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CRMClientCreate {
  email: string;
  displayName: string;
  phone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string;
  status?: CRMClientStatus;
  source?: string | null;
  sourceDetails?: Record<string, unknown>;
  tags?: string[];
  notes?: string | null;
}

export interface CRMClientUpdate {
  displayName?: string;
  phone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string;
  status?: CRMClientStatus;
  tags?: string[];
  notes?: string | null;
}

export interface CRMClientListItem {
  clientId: string;
  email: string;
  displayName: string;
  status: CRMClientStatus;
  tags: string[];
  createdAt: string;
}

export interface CRMClientListResponse {
  items: CRMClientListItem[];
  total: number;
  limit: number;
  offset: number;
}

// CRM Case Types
export interface CRMCase {
  caseId: string;
  tenantId: string;
  clientId: string;
  advisorId: string;
  referenceNumber: string;
  title: string;
  description: string | null;
  caseType: string | null;
  status: CRMCaseStatus;
  priority: CRMCasePriority;
  dueDate: string | null;
  quotedAmountCents: number | null;
  finalAmountCents: number | null;
  linkedDocuments: string[];
  tags: string[];
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
}

export interface CRMCaseCreate {
  clientId: string;
  title: string;
  description?: string | null;
  caseType?: string | null;
  priority?: CRMCasePriority;
  dueDate?: string | null;
  quotedAmountCents?: number | null;
  tags?: string[];
}

export interface CRMCaseUpdate {
  title?: string;
  description?: string | null;
  caseType?: string | null;
  status?: CRMCaseStatus;
  priority?: CRMCasePriority;
  dueDate?: string | null;
  quotedAmountCents?: number | null;
  finalAmountCents?: number | null;
  tags?: string[];
}

export interface CRMCaseListItem {
  caseId: string;
  title: string;
  referenceNumber: string;
  status: CRMCaseStatus;
  priority: CRMCasePriority;
  dueDate: string | null;
  createdAt: string;
}

export interface CRMCaseListResponse {
  items: CRMCaseListItem[];
  total: number;
  limit: number;
  offset: number;
}

// CRM Opportunity Types
export interface CRMOpportunity {
  opportunityId: string;
  tenantId: string;
  advisorId: string;
  clientId: string | null;
  contactEmail: string;
  contactName: string;
  contactPhone: string | null;
  title: string;
  description: string | null;
  serviceType: string | null;
  stage: CRMOpportunityStage;
  estimatedValueCents: number | null;
  probabilityPercent: number | null;
  source: string | null;
  sourceDetails: Record<string, unknown>;
  expectedCloseDate: string | null;
  lostReason: string | null;
  lostNotes: string | null;
  createdAt: string;
  updatedAt: string;
  convertedAt: string | null;
  closedAt: string | null;
}

export interface CRMOpportunityCreate {
  contactEmail: string;
  contactName: string;
  contactPhone?: string | null;
  title: string;
  description?: string | null;
  serviceType?: string | null;
  estimatedValueCents?: number | null;
  probabilityPercent?: number | null;
  source?: string | null;
  sourceDetails?: Record<string, unknown>;
  expectedCloseDate?: string | null;
}

export interface CRMOpportunityUpdate {
  contactName?: string;
  contactPhone?: string | null;
  title?: string;
  description?: string | null;
  serviceType?: string | null;
  stage?: CRMOpportunityStage;
  estimatedValueCents?: number | null;
  probabilityPercent?: number | null;
  expectedCloseDate?: string | null;
  lostReason?: string | null;
  lostNotes?: string | null;
}

export interface CRMOpportunityListItem {
  opportunityId: string;
  title: string;
  contactName: string;
  stage: CRMOpportunityStage;
  estimatedValueCents: number | null;
  expectedCloseDate: string | null;
  createdAt: string;
}

export interface CRMOpportunityListResponse {
  items: CRMOpportunityListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface CRMConvertOpportunityRequest {
  createCase?: boolean;
  caseTitle?: string;
}

export interface CRMConvertOpportunityResponse {
  client: CRMClient;
  case: CRMCase | null;
  opportunity: CRMOpportunity;
}

// CRM Task Types
export interface CRMTask {
  taskId: string;
  tenantId: string;
  advisorId: string;
  clientId: string | null;
  caseId: string | null;
  opportunityId: string | null;
  title: string;
  description: string | null;
  taskType: CRMTaskType;
  dueDate: string | null;
  dueTime: string | null;
  reminderAt: string | null;
  priority: CRMTaskPriority;
  status: CRMTaskStatus;
  completedAt: string | null;
  completionNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CRMTaskCreate {
  title: string;
  description?: string | null;
  taskType?: CRMTaskType;
  dueDate?: string | null;
  dueTime?: string | null;
  reminderAt?: string | null;
  priority?: CRMTaskPriority;
  clientId?: string | null;
  caseId?: string | null;
  opportunityId?: string | null;
}

export interface CRMTaskUpdate {
  title?: string;
  description?: string | null;
  taskType?: CRMTaskType;
  dueDate?: string | null;
  dueTime?: string | null;
  reminderAt?: string | null;
  priority?: CRMTaskPriority;
  status?: CRMTaskStatus;
}

export interface CRMTaskListItem {
  taskId: string;
  title: string;
  taskType: CRMTaskType;
  dueDate: string | null;
  priority: CRMTaskPriority;
  status: CRMTaskStatus;
}

export interface CRMTaskListResponse {
  items: CRMTaskListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface CRMTaskCompleteRequest {
  notes?: string;
}

// CRM Activity Types
export interface CRMActivity {
  activityId: string;
  tenantId: string;
  advisorId: string;
  clientId: string | null;
  caseId: string | null;
  opportunityId: string | null;
  activityType: CRMActivityType;
  subject: string | null;
  content: string | null;
  durationMinutes: number | null;
  outcome: string | null;
  occurredAt: string | null;
  attachments: Array<Record<string, unknown>>;
  createdAt: string;
}

export interface CRMActivityCreate {
  activityType: CRMActivityType;
  subject?: string | null;
  content?: string | null;
  durationMinutes?: number | null;
  outcome?: string | null;
  occurredAt?: string | null;
  clientId?: string | null;
  caseId?: string | null;
  opportunityId?: string | null;
  attachments?: Array<Record<string, unknown>>;
}

export interface CRMActivityListResponse {
  items: CRMActivity[];
  total: number;
  limit: number;
  offset: number;
}

// CRM Note Types
export interface CRMNote {
  noteId: string;
  tenantId: string;
  advisorId: string;
  clientId: string | null;
  caseId: string | null;
  title: string | null;
  content: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CRMNoteCreate {
  title?: string | null;
  content: string;
  isPinned?: boolean;
  clientId?: string | null;
  caseId?: string | null;
}

export interface CRMNoteUpdate {
  title?: string | null;
  content?: string;
  isPinned?: boolean;
}

export interface CRMNoteListResponse {
  items: CRMNote[];
  total: number;
  limit: number;
  offset: number;
}

// CRM Timeline Types
export interface CRMTimelineItem {
  type: 'activity' | 'case' | 'task';
  timestamp: string;
  data: Record<string, unknown>;
}

export interface CRMClientTimeline {
  clientId: string;
  items: CRMTimelineItem[];
  total: number;
}

export const crmApi = {
  // ===========================================================================
  // Clients
  // ===========================================================================

  /**
   * Create CRM client
   */
  createClient: async (data: CRMClientCreate): Promise<CRMClient> => {
    return fetchApi<CRMClient>('/crm/clients', {
      method: 'POST',
      body: JSON.stringify({
        email: data.email,
        display_name: data.displayName,
        phone: data.phone,
        address_line1: data.addressLine1,
        address_line2: data.addressLine2,
        postal_code: data.postalCode,
        city: data.city,
        country: data.country ?? 'NL',
        status: data.status ?? 'active',
        source: data.source,
        source_details: data.sourceDetails ?? {},
        tags: data.tags ?? [],
        notes: data.notes,
      }),
    });
  },

  /**
   * List CRM clients
   */
  listClients: async (params: {
    status?: CRMClientStatus;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<CRMClientListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.set('status', params.status);
    if (params.search) searchParams.set('search', params.search);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return fetchApi<CRMClientListResponse>(`/crm/clients${query ? `?${query}` : ''}`);
  },

  /**
   * Get CRM client by ID
   */
  getClient: async (clientId: string): Promise<CRMClient> => {
    return fetchApi<CRMClient>(`/crm/clients/${clientId}`);
  },

  /**
   * Update CRM client
   */
  updateClient: async (clientId: string, data: CRMClientUpdate): Promise<CRMClient> => {
    const body: Record<string, unknown> = {};
    if (data.displayName !== undefined) body.display_name = data.displayName;
    if (data.phone !== undefined) body.phone = data.phone;
    if (data.addressLine1 !== undefined) body.address_line1 = data.addressLine1;
    if (data.addressLine2 !== undefined) body.address_line2 = data.addressLine2;
    if (data.postalCode !== undefined) body.postal_code = data.postalCode;
    if (data.city !== undefined) body.city = data.city;
    if (data.country !== undefined) body.country = data.country;
    if (data.status !== undefined) body.status = data.status;
    if (data.tags !== undefined) body.tags = data.tags;
    if (data.notes !== undefined) body.notes = data.notes;

    return fetchApi<CRMClient>(`/crm/clients/${clientId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  /**
   * Get client timeline
   */
  getClientTimeline: async (clientId: string, params: {
    limit?: number;
    offset?: number;
  } = {}): Promise<CRMClientTimeline> => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return fetchApi<CRMClientTimeline>(`/crm/clients/${clientId}/timeline${query ? `?${query}` : ''}`);
  },

  // ===========================================================================
  // Cases
  // ===========================================================================

  /**
   * Create CRM case
   */
  createCase: async (data: CRMCaseCreate): Promise<CRMCase> => {
    return fetchApi<CRMCase>('/crm/cases', {
      method: 'POST',
      body: JSON.stringify({
        client_id: data.clientId,
        title: data.title,
        description: data.description,
        case_type: data.caseType,
        priority: data.priority ?? 'normal',
        due_date: data.dueDate,
        quoted_amount_cents: data.quotedAmountCents,
        tags: data.tags ?? [],
      }),
    });
  },

  /**
   * List CRM cases
   */
  listCases: async (params: {
    clientId?: string;
    status?: CRMCaseStatus;
    limit?: number;
    offset?: number;
  } = {}): Promise<CRMCaseListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.clientId) searchParams.set('client_id', params.clientId);
    if (params.status) searchParams.set('status', params.status);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return fetchApi<CRMCaseListResponse>(`/crm/cases${query ? `?${query}` : ''}`);
  },

  /**
   * Get CRM case by ID
   */
  getCase: async (caseId: string): Promise<CRMCase> => {
    return fetchApi<CRMCase>(`/crm/cases/${caseId}`);
  },

  /**
   * Update CRM case
   */
  updateCase: async (caseId: string, data: CRMCaseUpdate): Promise<CRMCase> => {
    const body: Record<string, unknown> = {};
    if (data.title !== undefined) body.title = data.title;
    if (data.description !== undefined) body.description = data.description;
    if (data.caseType !== undefined) body.case_type = data.caseType;
    if (data.status !== undefined) body.status = data.status;
    if (data.priority !== undefined) body.priority = data.priority;
    if (data.dueDate !== undefined) body.due_date = data.dueDate;
    if (data.quotedAmountCents !== undefined) body.quoted_amount_cents = data.quotedAmountCents;
    if (data.finalAmountCents !== undefined) body.final_amount_cents = data.finalAmountCents;
    if (data.tags !== undefined) body.tags = data.tags;

    return fetchApi<CRMCase>(`/crm/cases/${caseId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  /**
   * Link documents to case
   */
  linkDocumentsToCase: async (caseId: string, documentIds: string[]): Promise<CRMCase> => {
    return fetchApi<CRMCase>(`/crm/cases/${caseId}/documents`, {
      method: 'POST',
      body: JSON.stringify({ document_ids: documentIds }),
    });
  },

  // ===========================================================================
  // Opportunities
  // ===========================================================================

  /**
   * Create CRM opportunity
   */
  createOpportunity: async (data: CRMOpportunityCreate): Promise<CRMOpportunity> => {
    return fetchApi<CRMOpportunity>('/crm/opportunities', {
      method: 'POST',
      body: JSON.stringify({
        contact_email: data.contactEmail,
        contact_name: data.contactName,
        contact_phone: data.contactPhone,
        title: data.title,
        description: data.description,
        service_type: data.serviceType,
        estimated_value_cents: data.estimatedValueCents,
        probability_percent: data.probabilityPercent,
        source: data.source,
        source_details: data.sourceDetails ?? {},
        expected_close_date: data.expectedCloseDate,
      }),
    });
  },

  /**
   * List CRM opportunities
   */
  listOpportunities: async (params: {
    stage?: CRMOpportunityStage;
    limit?: number;
    offset?: number;
  } = {}): Promise<CRMOpportunityListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.stage) searchParams.set('stage', params.stage);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return fetchApi<CRMOpportunityListResponse>(`/crm/opportunities${query ? `?${query}` : ''}`);
  },

  /**
   * Get CRM opportunity by ID
   */
  getOpportunity: async (opportunityId: string): Promise<CRMOpportunity> => {
    return fetchApi<CRMOpportunity>(`/crm/opportunities/${opportunityId}`);
  },

  /**
   * Update CRM opportunity
   */
  updateOpportunity: async (opportunityId: string, data: CRMOpportunityUpdate): Promise<CRMOpportunity> => {
    const body: Record<string, unknown> = {};
    if (data.contactName !== undefined) body.contact_name = data.contactName;
    if (data.contactPhone !== undefined) body.contact_phone = data.contactPhone;
    if (data.title !== undefined) body.title = data.title;
    if (data.description !== undefined) body.description = data.description;
    if (data.serviceType !== undefined) body.service_type = data.serviceType;
    if (data.stage !== undefined) body.stage = data.stage;
    if (data.estimatedValueCents !== undefined) body.estimated_value_cents = data.estimatedValueCents;
    if (data.probabilityPercent !== undefined) body.probability_percent = data.probabilityPercent;
    if (data.expectedCloseDate !== undefined) body.expected_close_date = data.expectedCloseDate;
    if (data.lostReason !== undefined) body.lost_reason = data.lostReason;
    if (data.lostNotes !== undefined) body.lost_notes = data.lostNotes;

    return fetchApi<CRMOpportunity>(`/crm/opportunities/${opportunityId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  /**
   * Convert opportunity to client
   */
  convertOpportunity: async (opportunityId: string, data: CRMConvertOpportunityRequest): Promise<CRMConvertOpportunityResponse> => {
    return fetchApi<CRMConvertOpportunityResponse>(`/crm/opportunities/${opportunityId}/convert`, {
      method: 'POST',
      body: JSON.stringify({
        create_case: data.createCase ?? false,
        case_title: data.caseTitle,
      }),
    });
  },

  // ===========================================================================
  // Tasks
  // ===========================================================================

  /**
   * Create CRM task
   */
  createTask: async (data: CRMTaskCreate): Promise<CRMTask> => {
    return fetchApi<CRMTask>('/crm/tasks', {
      method: 'POST',
      body: JSON.stringify({
        title: data.title,
        description: data.description,
        task_type: data.taskType ?? 'general',
        due_date: data.dueDate,
        due_time: data.dueTime,
        reminder_at: data.reminderAt,
        priority: data.priority ?? 'medium',
        client_id: data.clientId,
        case_id: data.caseId,
        opportunity_id: data.opportunityId,
      }),
    });
  },

  /**
   * List CRM tasks
   */
  listTasks: async (params: {
    clientId?: string;
    caseId?: string;
    status?: CRMTaskStatus;
    dueBefore?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<CRMTaskListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.clientId) searchParams.set('client_id', params.clientId);
    if (params.caseId) searchParams.set('case_id', params.caseId);
    if (params.status) searchParams.set('status', params.status);
    if (params.dueBefore) searchParams.set('due_before', params.dueBefore);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return fetchApi<CRMTaskListResponse>(`/crm/tasks${query ? `?${query}` : ''}`);
  },

  /**
   * Get CRM task by ID
   */
  getTask: async (taskId: string): Promise<CRMTask> => {
    return fetchApi<CRMTask>(`/crm/tasks/${taskId}`);
  },

  /**
   * Update CRM task
   */
  updateTask: async (taskId: string, data: CRMTaskUpdate): Promise<CRMTask> => {
    const body: Record<string, unknown> = {};
    if (data.title !== undefined) body.title = data.title;
    if (data.description !== undefined) body.description = data.description;
    if (data.taskType !== undefined) body.task_type = data.taskType;
    if (data.dueDate !== undefined) body.due_date = data.dueDate;
    if (data.dueTime !== undefined) body.due_time = data.dueTime;
    if (data.reminderAt !== undefined) body.reminder_at = data.reminderAt;
    if (data.priority !== undefined) body.priority = data.priority;
    if (data.status !== undefined) body.status = data.status;

    return fetchApi<CRMTask>(`/crm/tasks/${taskId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  /**
   * Complete CRM task
   */
  completeTask: async (taskId: string, data?: CRMTaskCompleteRequest): Promise<CRMTask> => {
    return fetchApi<CRMTask>(`/crm/tasks/${taskId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ notes: data?.notes }),
    });
  },

  // ===========================================================================
  // Activities
  // ===========================================================================

  /**
   * Log CRM activity
   */
  logActivity: async (data: CRMActivityCreate): Promise<CRMActivity> => {
    return fetchApi<CRMActivity>('/crm/activities', {
      method: 'POST',
      body: JSON.stringify({
        activity_type: data.activityType,
        subject: data.subject,
        content: data.content,
        duration_minutes: data.durationMinutes,
        outcome: data.outcome,
        occurred_at: data.occurredAt,
        client_id: data.clientId,
        case_id: data.caseId,
        opportunity_id: data.opportunityId,
        attachments: data.attachments ?? [],
      }),
    });
  },

  /**
   * List CRM activities
   */
  listActivities: async (params: {
    clientId?: string;
    caseId?: string;
    activityType?: CRMActivityType;
    limit?: number;
    offset?: number;
  } = {}): Promise<CRMActivityListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.clientId) searchParams.set('client_id', params.clientId);
    if (params.caseId) searchParams.set('case_id', params.caseId);
    if (params.activityType) searchParams.set('activity_type', params.activityType);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return fetchApi<CRMActivityListResponse>(`/crm/activities${query ? `?${query}` : ''}`);
  },

  // ===========================================================================
  // Notes
  // ===========================================================================

  /**
   * Create CRM note
   */
  createNote: async (data: CRMNoteCreate): Promise<CRMNote> => {
    return fetchApi<CRMNote>('/crm/notes', {
      method: 'POST',
      body: JSON.stringify({
        title: data.title,
        content: data.content,
        is_pinned: data.isPinned ?? false,
        client_id: data.clientId,
        case_id: data.caseId,
      }),
    });
  },

  /**
   * List CRM notes
   */
  listNotes: async (params: {
    clientId?: string;
    caseId?: string;
    pinnedOnly?: boolean;
    limit?: number;
    offset?: number;
  } = {}): Promise<CRMNoteListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.clientId) searchParams.set('client_id', params.clientId);
    if (params.caseId) searchParams.set('case_id', params.caseId);
    if (params.pinnedOnly) searchParams.set('pinned_only', String(params.pinnedOnly));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return fetchApi<CRMNoteListResponse>(`/crm/notes${query ? `?${query}` : ''}`);
  },

  /**
   * Update CRM note
   */
  updateNote: async (noteId: string, data: CRMNoteUpdate): Promise<CRMNote> => {
    const body: Record<string, unknown> = {};
    if (data.title !== undefined) body.title = data.title;
    if (data.content !== undefined) body.content = data.content;
    if (data.isPinned !== undefined) body.is_pinned = data.isPinned;

    return fetchApi<CRMNote>(`/crm/notes/${noteId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  /**
   * Delete CRM note
   */
  deleteNote: async (noteId: string): Promise<void> => {
    await fetchApi(`/crm/notes/${noteId}`, { method: 'DELETE' });
  },
};

/**
 * Consumer-facing questionnaire responses API
 */
export const questionnaireResponseApi = {
  /**
   * Start a questionnaire response (creates session)
   */
  start: async (data: ResponseStartRequest): Promise<ResponseStartResponse> => {
    return fetchApi<ResponseStartResponse>('/questionnaire-responses/start', {
      method: 'POST',
      body: JSON.stringify({
        questionnaire_id: data.questionnaireId,
        language: data.language,
        session_token: data.sessionToken,
      }),
    });
  },

  /**
   * Submit an answer
   */
  submitAnswer: async (responseId: string, data: AnswerSubmitRequest): Promise<void> => {
    await fetchApi(`/questionnaire-responses/${responseId}/answers`, {
      method: 'POST',
      body: JSON.stringify({
        question_id: data.questionId,
        value: data.value,
      }),
    });
  },

  /**
   * Save current progress
   */
  saveProgress: async (responseId: string, currentStep: number): Promise<void> => {
    await fetchApi(`/questionnaire-responses/${responseId}/progress`, {
      method: 'PUT',
      body: JSON.stringify({ current_step: currentStep }),
    });
  },

  /**
   * Complete the questionnaire
   */
  complete: async (responseId: string): Promise<{ completedAt: string }> => {
    return fetchApi(`/questionnaire-responses/${responseId}/complete`, {
      method: 'POST',
    });
  },

  /**
   * Get response by ID (for resuming)
   */
  get: async (responseId: string): Promise<ResponseStartResponse> => {
    return fetchApi<ResponseStartResponse>(`/questionnaire-responses/${responseId}`);
  },

  /**
   * Switch language
   */
  switchLanguage: async (responseId: string, language: 'nl' | 'en'): Promise<void> => {
    await fetchApi(`/questionnaire-responses/${responseId}/language`, {
      method: 'PUT',
      body: JSON.stringify({ language }),
    });
  },
};
